import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import ReactDOM from 'react-dom';

export type StateBase = object;
export type ActionBase = object;
export type Selector<S extends StateBase, R> = (state: S) => R;
export type EqualityChecker<R> = (oldSelection: R, newSelection: R) => boolean;
export type UseSelectedState<S extends StateBase> = <R>(selector: Selector<S, R>, isEqual?: EqualityChecker<R>) => R;

export function BnSelectedStateProvider<S extends StateBase>({ state, UseSelectedStateContext, children }: {
  state: S,
  UseSelectedStateContext: React.Context<UseSelectedState<S>>,
  children: React.ReactNode,
}) {
  const stateRef = useRef(state);
  stateRef.current = state;
  const target = useMemo(() => new ContextStateSelectorTarget(), []);
  const useContextStateSelector = useMemo(() => createContextStateSelectorHook(target, stateRef), [target, stateRef]);

  useEffect(
    function observeState () {
      // Whenever the contexts state is updated, fire an event which is handled by uses of `useContextStateSelector`
      ReactDOM.unstable_batchedUpdates(() => {
        target.dispatchEvent(new CustomEvent('state-updated', { detail: state }));
      });
    },
    [state, target],
  );

  return <UseSelectedStateContext.Provider value={useContextStateSelector}>
    {children}
  </UseSelectedStateContext.Provider>;
}

class ContextStateSelectorTarget extends EventTarget {}

function createContextStateSelectorHook<S extends StateBase>(eventTarget: ContextStateSelectorTarget, stateRef: RefObject<S>) {
  const subscribe = (cb: () => void) => {
    // React doesn't explicitly call out whether the callback is different each time, so it could be dangerous for
    // us to assume that it is (e.g. maybe there is another hook observing that eventTarget with useSyncExternalStore)
    // We'll wrap the callback in a function that will be unique to each re-subscription
    const uniqueCb = () => cb();
    eventTarget.addEventListener('state-updated', uniqueCb);
    return () => eventTarget.removeEventListener('state-updated', uniqueCb);
  };
  const getSnapshot = () => stateRef.current as S;

  return function useSelectedState<R>(selector: Selector<S, R>, isEqual = _.isEqual) {
    return useSyncExternalStoreWithSelector(
      subscribe,
      getSnapshot,
      null, // getServerSnapshot
      selector,
      isEqual,
    );
  };
}
